import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fm } from '../../utils';
import KpiCard from '../reports/KpiCard';
import PaymentChart from '../reports/PaymentChart';
import { TopProducts } from '../reports/TopProducts';
import { WaiterStats } from '../reports/WaiterStats';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, BarChart, Bar } from 'recharts';
import { motion } from 'framer-motion';
import {
  DollarSign, ShoppingCart, Users, Bell, User,
  TrendingUp, TrendingDown
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '/api';
const toDateStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

const DashboardHeader = ({ kullanici, ozet, dunOzet, canliSaat }) => {
  const dunCiro = dunOzet?.toplamCiro || 0;
  const bugunCiro = ozet?.toplamCiro || 0;
  const trend = dunCiro > 0 ? Math.round(((bugunCiro - dunCiro) / dunCiro) * 100) : null;

  return (
    <motion.div variants={itemAnim} className="bg-gradient-to-br from-surface-900 via-surface-800 to-emerald-900 rounded-3xl p-5 sm:p-6 text-white shadow-soft-xl relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(16,185,129,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(99,102,241,0.1),transparent_50%)]" />
      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/10">
              <User size={18} className="text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg sm:text-xl text-white">
                Günaydın, {kullanici?.adSoyad?.split(' ')[0] || 'Yönetici'} 👋
              </h2>
              <p className="text-xs text-white/60 mt-0.5">
                {new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} • {canliSaat}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10 flex items-center gap-2">
              <Bell size={14} className="text-white/60" />
              <span className="w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center">3</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 border border-white/10">
              <span className="text-[11px] text-white/70">Admin Panel</span>
            </div>
          </div>
        </div>
        {trend !== null && (
          <div className="flex items-center gap-2 mt-3 bg-white/5 rounded-xl px-3.5 py-2 backdrop-blur-sm border border-white/5">
            {trend >= 0 ? <TrendingUp size={14} className="text-emerald-400" /> : <TrendingDown size={14} className="text-red-400" />}
            <span className="text-xs text-white/80">
              Bugün satışlar düne göre <span className={`font-bold ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>%{trend > 0 ? '+' : ''}{trend}</span> {trend >= 0 ? 'daha yüksek' : 'daha düşük'}
            </span>
            <span className="text-[10px] text-white/40 ml-auto">Canlı • Güncelleniyor</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

const KpiCards = ({ gunlukOzet, haftalikCiro, aylikCiro, toplamSiparis, aktifMasa, trendDun }) => {
  const kpis = [
    { label: 'Günlük Ciro', value: fm(gunlukOzet?.toplamCiro || 0), icon: <DollarSign size={16} />, color: 'emerald', trend: trendDun?.ciro, sparkline: true },
    { label: 'Haftalık Ciro', value: fm(haftalikCiro), icon: <TrendingUp size={16} />, color: 'blue' },
    { label: 'Aylık Ciro', value: fm(aylikCiro), icon: <TrendingUp size={16} />, color: 'purple' },
    { label: 'Toplam Sipariş', value: toplamSiparis, icon: <ShoppingCart size={16} />, color: 'amber', trend: trendDun?.siparis },
    { label: 'Dolu Masa', value: aktifMasa, icon: <Users size={16} />, color: 'emerald', sub: `${aktifMasa} masa dolu` },
  ];
  return (
    <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3">
      {kpis.map((k, i) => (
        <motion.div key={i} variants={itemAnim}>
          <KpiCard label={k.label} value={k.value} icon={k.icon} color={k.color} trend={k.trend} trendLabel="düne göre" sub={k.sub} sparklineData={k.sparkline ? Array.from({ length: 7 }, () => Math.floor(Math.random() * 1000) + 200) : []} />
        </motion.div>
      ))}
    </motion.div>
  );
};

const SalesChart = ({ data = [], timeFilter, setTimeFilter, title = 'Satış Grafiği' }) => {
  const filters = [
    { key: 'hourly', label: 'Saatlik' },
    { key: 'daily', label: 'Günlük' },
    { key: 'weekly', label: 'Haftalık' },
    { key: 'monthly', label: 'Aylık' },
  ];
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl shadow-lg p-3 text-xs">
        <p className="font-semibold text-surface-700 dark:text-surface-300 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-surface-500" style={{ color: p.color }}>
            {p.name}: <span className="font-semibold">{fm(p.value)}</span>
          </p>
        ))}
      </div>
    );
  };
  return (
    <motion.div variants={itemAnim} className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-semibold text-surface-400 uppercase tracking-wider">{title}</h4>
        <div className="flex gap-1">
          {filters.map(f => (
            <button key={f.key} onClick={() => setTimeFilter(f.key)}
              className={`px-2 py-1 text-[10px] font-semibold rounded-lg transition-all ${timeFilter === f.key ? 'bg-emerald-500 text-white' : 'text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {data.length === 0 ? (
        <div className="h-52 flex items-center justify-center text-xs text-surface-300">Veri bulunamadı</div>
      ) : (
        <div className="h-52">
          <ResponsiveContainer>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="salesGrad2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => fm(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="tutar" stroke="#10b981" fill="url(#salesGrad2)" strokeWidth={2} name="Tutar" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </motion.div>
  );
};



const TableStatus = ({ masalar = [] }) => {
  const dolu = masalar.filter(m => m.durum === 'Dolu').length;
  const bos = masalar.filter(m => m.durum === 'Boş').length;
  const rezerve = masalar.filter(m => m.durum === 'Rezerve').length;
  return (
    <motion.div variants={itemAnim} className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
      <h4 className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider mb-3">Masa Durumu</h4>
      <div className="flex gap-2 mb-3">
        <div className="flex-1 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl p-2 text-center">
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{dolu}</p>
          <p className="text-[9px] text-emerald-500/70">Dolu</p>
        </div>
        <div className="flex-1 bg-surface-100 dark:bg-surface-700 rounded-xl p-2 text-center">
          <p className="text-lg font-bold text-surface-600 dark:text-surface-400">{bos}</p>
          <p className="text-[9px] text-surface-400">Boş</p>
        </div>
        <div className="flex-1 bg-purple-50 dark:bg-purple-900/10 rounded-xl p-2 text-center">
          <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{rezerve}</p>
          <p className="text-[9px] text-purple-500/70">Rezerve</p>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {masalar.map((m, i) => (
          <div key={i} className={`h-7 rounded-lg flex items-center justify-center text-[8px] font-bold ${
            m.durum === 'Dolu' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
            m.durum === 'Rezerve' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
            'bg-surface-100 text-surface-400 dark:bg-surface-700 dark:text-surface-500'
          }`}>{m.masaNumarasi || m.masaID}</div>
        ))}
      </div>
    </motion.div>
  );
};





const DensityAnalysis = ({ saatlikVeri = [] }) => {
  const maxAdet = Math.max(...saatlikVeri.map(d => d.adet || 0), 1);
  return (
    <motion.div variants={itemAnim} className="bg-white dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 shadow-soft p-4">
      <h4 className="text-[10px] font-semibold text-surface-400 uppercase tracking-wider mb-3">Yoğunluk Analizi</h4>
      {saatlikVeri.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-xs text-surface-300">Veri bulunamadı</div>
      ) : (
        <div className="h-32">
          <ResponsiveContainer>
            <BarChart data={saatlikVeri}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={2} />
              <YAxis hide />
              <Tooltip formatter={(v) => `${v} sipariş`} />
              <Bar dataKey="adet" radius={[4, 4, 0, 0]} maxBarSize={24}>
                {saatlikVeri.map((d, i) => {
                  const pct = maxAdet > 0 ? d.adet / maxAdet : 0;
                  return <rect key={i} fill={pct > 0.7 ? '#10b981' : pct > 0.4 ? '#34d399' : pct > 0.2 ? '#6ee7b7' : '#d1fae5'} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {saatlikVeri.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-3">
          <div className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-2 text-center">
            <p className="text-[9px] text-surface-400">En Yoğun Saat</p>
            <p className="text-xs font-bold text-surface-800 dark:text-white">{saatlikVeri.reduce((a, b) => (a.adet || 0) > (b.adet || 0) ? a : b, { adet: 0 }).label || '-'}</p>
          </div>
          <div className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-2 text-center">
            <p className="text-[9px] text-surface-400">En Yüksek Ciro</p>
            <p className="text-xs font-bold text-surface-800 dark:text-white">{fm(Math.max(...saatlikVeri.map(d => d.tutar || 0)))}</p>
          </div>
          <div className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-2 text-center">
            <p className="text-[9px] text-surface-400">Toplam Sipariş</p>
            <p className="text-xs font-bold text-surface-800 dark:text-white">{saatlikVeri.reduce((t, d) => t + (d.adet || 0), 0)}</p>
          </div>
          <div className="bg-surface-50 dark:bg-surface-700/30 rounded-xl p-2 text-center">
            <p className="text-[9px] text-surface-400">Ort. Saatlik</p>
            <p className="text-xs font-bold text-surface-800 dark:text-white">{Math.round(saatlikVeri.reduce((t, d) => t + (d.adet || 0), 0) / Math.max(saatlikVeri.length, 1))}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const DashboardMain = () => {
  const { token, kullanici } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('hourly');
  const [canliSaat, setCanliSaat] = useState('');
  const [gunlukOzet, setGunlukOzet] = useState(null);
  const [dunOzet, setDunOzet] = useState(null);
  const [gelirGider, setGelirGider] = useState([]);
  const [enCokSatilan, setEnCokSatilan] = useState([]);
  const [odemeDagilimi, setOdemeDagilimi] = useState([]);
  const [garsonSatislari, setGarsonSatislari] = useState([]);
  const [masaDurumu, setMasaDurumu] = useState([]);
  const [saatlikVeri, setSaatlikVeri] = useState([]);

  const fetchJson = useCallback(async (url, opts) => {
    const hh = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    try { const r = await fetch(url, opts || { headers: hh }); return r.ok ? await r.json() : null; }
    catch (e) { console.error('Fetch error:', url, e); return null; }
  }, [token]);

  const fetchAll = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    const today = toDateStr(new Date());
    const y = new Date(); y.setDate(y.getDate() - 1);
    const yesterday = toDateStr(y);
    const m = new Date(); m.setDate(1);
    const monthStart = toDateStr(m);

    const [ozet, ozetDun, gelir, top, odeme, garson, masa] = await Promise.all([
      fetchJson(`${API}/raporlar/gunluk-ozet?tarih=${today}`),
      fetchJson(`${API}/raporlar/gunluk-ozet?tarih=${yesterday}`),
      fetchJson(`${API}/raporlar/gelir-gider?baslangic=${monthStart}&bitis=${today}`),
      fetchJson(`${API}/raporlar/en-cok-satilan?limit=10`),
      fetchJson(`${API}/raporlar/odeme-dagilimi?baslangic=${monthStart}&bitis=${today}`),
      fetchJson(`${API}/raporlar/garson-satislari?baslangic=${today}&bitis=${today}`),
      fetchJson(`${API}/raporlar/masa-durumu`),
    ]);

    setGunlukOzet(ozet);
    setDunOzet(ozetDun);
    setGelirGider(gelir?.gunluk || []);
    setEnCokSatilan(top || []);
    setOdemeDagilimi((odeme || []).map(o => ({ name: o.odemeTuru, value: o.toplamTutar, yuzde: o.yuzde, adet: o.adet })));
    setGarsonSatislari(garson || []);
    setMasaDurumu(masa || []);
    setSaatlikVeri(ozet?.saatlikDagilim?.map(s => ({ saat: s.saat, label: `${String(s.saat).padStart(2, '0')}:00`, adet: s.adet, tutar: s.tutar })) || []);
    if (!silent) setLoading(false);
  }, [fetchJson]);

  useEffect(() => { fetchAll(); const iv = setInterval(() => fetchAll(true), 30000); return () => clearInterval(iv); }, [fetchAll]);

  useEffect(() => {
    const tick = () => setCanliSaat(new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick(); const iv = setInterval(tick, 1000); return () => clearInterval(iv);
  }, []);

  const haftalikCiro = gelirGider.reduce((t, d) => t + (d.toplam || 0), 0);
  const aylikCiro = haftalikCiro;
  const toplamSiparis = gunlukOzet?.siparisSayisi || 0;
  const aktifMasa = masaDurumu.filter(m => m.durum === 'Dolu').length;

  const trendDunCiro = dunOzet?.toplamCiro > 0 ? Math.round(((gunlukOzet?.toplamCiro || 0) - dunOzet.toplamCiro) / dunOzet.toplamCiro * 100) : null;
  const trendDunSiparis = dunOzet?.siparisSayisi > 0 ? Math.round(((toplamSiparis - dunOzet.siparisSayisi) / dunOzet.siparisSayisi) * 100) : null;

  const chartData = useMemo(() => {
    switch (timeFilter) {
      case 'hourly': return saatlikVeri;
      case 'daily': return gelirGider.map(d => ({ label: d.tarih ? new Date(d.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '', tutar: d.toplam || 0 }));
      case 'weekly': {
        const weeks = {};
        gelirGider.forEach(d => {
          const date = new Date(d.tarih);
          const wk = `H${Math.ceil((date.getDate()) / 7)}`;
          weeks[wk] = (weeks[wk] || 0) + (d.toplam || 0);
        });
        return Object.entries(weeks).map(([label, tutar]) => ({ label, tutar }));
      }
      case 'monthly': return gelirGider.map(d => ({ label: d.tarih ? new Date(d.tarih).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }) : '', tutar: d.toplam || 0 }));
      default: return saatlikVeri;
    }
  }, [timeFilter, saatlikVeri, gelirGider]);



  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-surface-400 font-medium">Pano yükleniyor...</span>
      </div>
    </div>
  );

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-4">
      {/* Header */}
      <DashboardHeader kullanici={kullanici} ozet={gunlukOzet} dunOzet={dunOzet} canliSaat={canliSaat} />

      {/* KPI Cards */}
      <KpiCards gunlukOzet={gunlukOzet} haftalikCiro={haftalikCiro} aylikCiro={aylikCiro}
        toplamSiparis={toplamSiparis} aktifMasa={aktifMasa} trendDun={{ ciro: trendDunCiro, siparis: trendDunSiparis }} />

      {/* Row: Sales + Payment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <SalesChart data={chartData} timeFilter={timeFilter} setTimeFilter={setTimeFilter} />
        </div>
        <div>
          <PaymentChart data={odemeDagilimi} />
        </div>
      </div>

      {/* Row: Products + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TopProducts data={enCokSatilan} />
        </div>
        <div>
          <TableStatus masalar={masaDurumu} />
        </div>
      </div>

      {/* Row: Waiter + Density */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div>
          <WaiterStats data={garsonSatislari} />
        </div>
        <div className="lg:col-span-2">
          <DensityAnalysis saatlikVeri={saatlikVeri} />
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardMain;
